import { has, get, groupBy, includes, isEmpty, reduce, set, without } from 'lodash';

import { AutoLinkFields, ExternalSourceType, ExternalSourceTypes } from 'src/actions/integrations/commonTypes';
import { ExternalSource, ExternalSourceSecrets, SearchField } from 'src/actions/integrations/integrationSources';
import { DomainError } from 'src/models/DomainError';
import {
  ActionsSupport,
  AutoLinkSupportForEmailAndPhone,
  CustomerLookupSupport,
  IntegrationDisplayLabels,
} from 'src/presentation/components/pages/integrations/constants';
import generateId from 'src/lib/generateId';
import { getActionDefaults } from 'src/presentation/components/pages/integrations/actionConfigurations';
import { NameValueList, NameValuePair } from 'src/presentation/components/pages/integrations/types';
import { toastError } from 'src/actions/toast';

/**
 * Builds a "default" (aka "new") App item
 *
 * @param itemType
 */
export function buildDefaultItem(itemType?: ExternalSourceType): Partial<ExternalSource> {
  const displayLabel = itemType ? IntegrationDisplayLabels[itemType] : '';
  const item: Partial<ExternalSource> = {
    id: generateId(),
    name: `New ${displayLabel} App`,
    type: itemType,
    enabled: true,
    connectionSettings: { baseUrl: '' },
    secrets: buildSecrets(itemType),
  };
  if (itemType === ExternalSourceTypes.BigCommerce) {
    set(item, 'connectionSettings.alwaysUseStoreCreditToRefund', false);
  }

  if (itemType === ExternalSourceTypes.AwsEventBridge) {
    item.customConfigurations = {
      awsAccountId: '',
      region: '',
      entityTypes: [],
    };
  }

  if (itemType && CustomerLookupSupport[itemType]) {
    item.customerLookupSettings = {
      overwritingEnabled: false,
      autoLinkFields: AutoLinkSupportForEmailAndPhone.includes(itemType)
        ? [AutoLinkFields.Email, AutoLinkFields.Phone]
        : [AutoLinkFields.Email],
      searchQueryAttributes: buildSearchQueryFields(itemType),
      searchResultAttributes: buildSearchResultFields(itemType),
    };
  }

  if (itemType && ActionsSupport[itemType]) {
    item.actionSettings = {
      enabled: true,
    };
  }

  return item;
}

/**
 * Converts "hasCustomHeaders" list into an array that can be consumed by the Configuration Editor
 *
 * @param item
 */
export function buildCustomHeaderList(item?: Partial<ExternalSource>): NameValueList {
  if (item?.type !== ExternalSourceTypes.Custom) return [];
  return (item.secrets?.hasCustomHeaders || []).map(name => ({ id: generateId(), name }));
}

export function searchAttrsToNameValueList(
  item: ExternalSource,
  attr: string,
  requiredFields: string[]
): NameValueList {
  if (!item || !attr) return [];
  if (!CustomerLookupSupport[item.type]) return [];

  const attrList = get(item, attr, []) as SearchField[];
  return attrList.map(item => ({
    id: generateId(),
    isRequired: includes(requiredFields, item.attr),
    name: item.attr,
    value: item.label,
  }));
}

export function nameValueListToSearchAttrs(list: NameValueList): SearchField[] {
  return list.map(item => ({
    attr: item.name,
    isRequired: !!item.isRequired,
    label: item.value || '',
  }));
}

/**
 * Checks the [<name,value>] list for duplicates, missing names, values etc. and returns
 * a list of DomainError objects for each error. Returns [] if no errors found.
 *
 * @param itemList - the list to validate
 * @param secretItemNames - list of items that are treated as "secrets", i.e. that are allowed
 * @param restrictedWords - list of "reserved" words that are not allowed to be used as an item name (e.g. "emails"
 * is a reserved word that has special meaning within Gladly and cannot be used as an attribute name)
 * to have value === undefined and with name that appears on the list of "secrets"
 * (usually, "hasCustomHeaders" flags) that was received from SN
 */
export function verifyNameValueList(
  itemList: NameValueList,
  secretItemNames: string[],
  restrictedWords: string[] = []
): DomainError[] {
  if (!itemList.length) return [];
  if (itemList.length === 1 && !itemList[0].name && !itemList[0].value) return [];

  // Filter out items that changed vs. the "secretItemNames" list
  const changed = itemList.filter(item => !(secretItemNames.includes(item.name) && item.value === undefined));

  // Find items with empty/missing values and invalid names, collect their internal IDs
  const emptyValues = changed.filter(item => (item.value || '').trim() === '').map(item => item.id);
  const invalidNames = itemList.filter(item => !isValidHeaderName(item.name)).map(item => item.id);
  const prohibitedItems = restrictedWords.length
    ? itemList.filter(item => restrictedWords.includes(item.name)).map(item => item.id)
    : [];

  // Find all headers with duplicate names
  const validNames = itemList.filter(item => isValidHeaderName(item.name));
  const groupByName = groupBy(validNames, item => item.name.trim());
  const duplicateNames = reduce(
    groupByName,
    (acc, group) => {
      if (group.length < 2) return acc;
      const ids = group.map(item => item.id);
      return [...acc, ...ids];
    },
    [] as string[]
  );

  // Now combine all the errors together
  const duplicatedNamesError = mapErrors(duplicateNames, 'name', 'taken', 'This name is taken. Try another.');
  const invalidNamesError = mapErrors(invalidNames, 'name', 'invalid', 'Please enter a valid name');
  const prohibitedNameErrors = mapErrors(
    prohibitedItems,
    'name',
    'invalid',
    'This name is already in use by a default Gladly field'
  );

  const valueErrors = mapErrors(emptyValues, 'value', 'blank', 'Please enter a valid value');

  return [...valueErrors, ...invalidNamesError, ...duplicatedNamesError, ...prohibitedNameErrors];
}

export function buildCustomHeaderMap(headerList: NameValueList, existingHeaders: string[]): Record<string, string> {
  const changed = headerList.filter(header => !(existingHeaders.includes(header.name) && header.value === undefined));
  if (!changed.length && headerList.length === existingHeaders.length) return {}; // No changes to the headers

  const editedHeaders = headerList.map(header => header.name);
  const deletedHeaders = without(existingHeaders, ...editedHeaders);

  const updatedHeadersMap = changed.reduce((acc, header) => {
    if (header.name) acc[header.name] = header.value;
    return acc;
  }, {});
  const deletedHeadersMap = deletedHeaders.reduce((acc, headerName) => {
    acc[headerName] = null;
    return acc;
  }, {});

  return { ...deletedHeadersMap, ...updatedHeadersMap };
}

export function redirectToUrl(redirectUrl, setRedirectPromptVisible, dispatch) {
  setRedirectPromptVisible(false);
  // React router locks us inside the list of configured routes, so we have to go around it
  // in order to navigate to the external Url
  if (!window.open(redirectUrl, '_blank')) {
    dispatch(toastError(`Please disable your content blocker or allow "${redirectUrl}"`));
  }
}

export function getOAuthRedirectURL(integrationType: ExternalSourceType) {
  const clusterNames = ['master', 'staging', 'production'];
  const gladlyConfig = window['gladlyConfig'] || {};
  const serviceType = integrationType.toLocaleLowerCase();
  const env = gladlyConfig['environment'] as string | undefined;

  // If the environment is not one of the known clusters, we assume local dev environment
  const hostname = env && clusterNames.includes(env) ? '' : 'http://localhost:3500';
  return `${hostname}/api/v1/auth-bridge/configure/oauth/${serviceType}`;
}

/**
 * Checks for actionSettings.customSettings and updates missing properties with defaults
 * @param integrationType
 * @param currentActionSettings
 */
export const updateUnsetActionSettings = (
  integrationType: ExternalSourceTypes | undefined,
  currentActionSettings: any
) => {
  if (!integrationType || !currentActionSettings) return;

  const actionDefaults = getActionDefaults(integrationType);
  if (isEmpty(actionDefaults)) return;

  const customSettingDefaults = reduce(
    actionDefaults,
    (acc, enabled, actionCode) => {
      acc[actionCode] = { enabled };
      return acc;
    },
    {}
  );

  if (!currentActionSettings.customSettings) {
    return {
      actionSettings: {
        enabled: currentActionSettings['enabled'],
        customSettings: { ...customSettingDefaults },
      },
    };
  } else {
    const updatedActionSettings = {
      actionSettings: { ...currentActionSettings },
    };

    for (const action in customSettingDefaults) {
      if (!has(updatedActionSettings.actionSettings, ['customSettings', action])) {
        set(updatedActionSettings.actionSettings, ['customSettings', action], customSettingDefaults[action]);
      }
    }
    return updatedActionSettings;
  }
};

// ================================= Internal Helpers =========================================

function buildSecrets(itemType?: ExternalSourceType): ExternalSourceSecrets {
  switch (itemType) {
    case ExternalSourceTypes.Magento:
      return {
        consumer: { key: '', secret: '' },
        token: { key: '', secret: '' },
      };

    case ExternalSourceTypes.Klaviyo:
      return {
        privateApiKey: '',
      };

    case ExternalSourceTypes.Custom:
      return {
        signingKey: '',
        credentials: {},
      };

    case ExternalSourceTypes.Ordergroove:
      return {
        apiKey: '',
      } as any;

    default:
      return { token: '' };
  }
}

function buildSearchQueryFields(itemType?: ExternalSourceType): SearchField[] {
  switch (itemType) {
    case ExternalSourceTypes.Shopify:
      return [
        { attr: 'email', label: 'Email' },
        { attr: 'phone', label: 'Phone Number' },
      ];
    case ExternalSourceTypes.Smile:
      return [{ attr: 'email', label: 'Email' }];

    case ExternalSourceTypes.Magento:
      return [{ attr: 'email', label: 'Email' }];

    case ExternalSourceTypes.BigCommerce:
      return [
        { attr: 'email', label: 'Email' },
        { attr: 'phone', label: 'Phone Number' },
      ];

    case ExternalSourceTypes.Recharge:
      return [{ attr: 'email', label: 'Email' }];

    default:
      return [];
  }
}

function buildSearchResultFields(itemType?: ExternalSourceType): SearchField[] {
  switch (itemType) {
    case ExternalSourceTypes.Shopify:
      return [
        { attr: 'name', label: 'Name' },
        { attr: 'email', label: 'Email' },
        { attr: 'externalCustomerId', label: 'Shopify ID' },
      ];

    case ExternalSourceTypes.Smile:
      return [
        { attr: 'name', label: 'Name' },
        { attr: 'email', label: 'Email' },
        { attr: 'externalCustomerId', label: 'Smile ID' },
      ];

    case ExternalSourceTypes.Magento:
      return [
        { attr: 'name', label: 'Name' },
        { attr: 'email', label: 'Email' },
        { attr: 'externalCustomerId', label: 'Magento ID' },
      ];

    case ExternalSourceTypes.BigCommerce:
      return [
        { attr: 'name', label: 'Name' },
        { attr: 'email', label: 'Email' },
      ];

    case ExternalSourceTypes.Recharge:
      return [
        { attr: 'name', label: 'Name' },
        { attr: 'email', label: 'Email' },
      ];

    default:
      return [];
  }
}

function mapErrors(itemIDs: string[], attr: keyof NameValuePair, code: string, detail: string): DomainError[] {
  return itemIDs.map(id => ({ code, attr: `${id}.${attr}`, detail } as DomainError));
}

function isValidHeaderName(name: string): boolean {
  const regex = /^\w+[\w_-]*$/g;
  return regex.test(name);
}
